import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaBroadcastTower, FaMapMarkerAlt, FaPlayCircle, FaSyncAlt } from 'react-icons/fa';
import { getOkamCameras, getOkamPlayback, getOkamStatus } from '../../api/okamService';

const statusLabels = {
  Loading: 'Kontrol ediliyor',
  Starting: 'Başlatılıyor',
  Connected: 'Bağlandı',
  Streaming: 'Canlı',
  Stopped: 'Yayın durduruldu',
  Unavailable: 'Yayın geçici olarak kullanılamıyor'
};

const normalizeStatus = (playback) => {
  const normalized = (playback?.state || '').trim().toLowerCase();

  if (normalized === 'streaming' && playback?.ffmpegRunning) return 'Streaming';
  if (normalized === 'streaming') return 'Starting';
  if (normalized === 'starting') return 'Starting';
  if (normalized === 'connected') return 'Connected';
  if (normalized === 'stopped') return 'Stopped';
  if (normalized === 'error' || playback?.lastError) return 'Unavailable';
  return 'Loading';
};

const OkamPlayer = ({ camera }) => {
  const videoRef = useRef(null);
  const [playerError, setPlayerError] = useState('');

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !camera?.hlsUrl || camera?.status !== 'Streaming') {
      setPlayerError('');
      if (video) {
        video.removeAttribute('src');
        video.load();
      }
      return undefined;
    }

    setPlayerError('');
    let hls;
    let disposed = false;
    let nativeRetryTimer;
    const tryPlay = () => {
      if (disposed) return;
      setPlayerError('');
      video.play().catch(() => {
        // Mobile browsers can still require a tap despite muted autoplay.
        // The native controls remain available in that case.
      });
    };
    const handleNativeError = () => {
      if (disposed) return;
      setPlayerError('Canlı yayın yeniden yükleniyor…');
      window.clearTimeout(nativeRetryTimer);
      nativeRetryTimer = window.setTimeout(() => {
        if (disposed) return;
        video.load();
        tryPlay();
      }, 2000);
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || disposed) return;
      if (hls) hls.startLoad(-1);
      tryPlay();
    };

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.defaultMuted = true;
      video.muted = true;
      video.src = camera.hlsUrl;
      video.load();
      video.addEventListener('loadedmetadata', tryPlay);
      video.addEventListener('canplay', tryPlay);
      video.addEventListener('error', handleNativeError);
    } else {
      import('hls.js').then(({ default: Hls }) => {
        if (disposed) return;

        if (!Hls.isSupported()) {
          setPlayerError('Tarayıcınız HLS canlı yayını desteklemiyor.');
          return;
        }

        hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30
        });
        hls.loadSource(camera.hlsUrl);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, tryPlay);
        hls.on(Hls.Events.ERROR, (_, data) => {
          if (!data.fatal) return;

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            setPlayerError('Canlı yayına bağlanılıyor…');
            hls.startLoad(-1);
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            setPlayerError('Görüntü yeniden hazırlanıyor…');
            hls.recoverMediaError();
          } else {
            setPlayerError('Canlı yayın şu anda oynatılamıyor.');
            hls.destroy();
          }
        });
      }).catch(() => {
        if (!disposed) setPlayerError('Canlı yayın oynatıcısı yüklenemedi.');
      });
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearTimeout(nativeRetryTimer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      video.removeEventListener('loadedmetadata', tryPlay);
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('error', handleNativeError);
      if (hls) hls.destroy();
      video.removeAttribute('src');
      video.load();
    };
  }, [camera?.hlsUrl, camera?.status]);

  return (
    <div className="okam-player">
      <video
        ref={videoRef}
        className="okam-player__video"
        controls
        autoPlay
        muted
        playsInline
        aria-label={`${camera?.name || 'Aile Köyü'} canlı kamera yayını`}
      />
      {camera?.status === 'Streaming' && (
        <div className="okam-player__live">
          <span className="okam-player__pulse" />
          CANLI
        </div>
      )}
      {playerError && (
        <div className="okam-player__message">
          <FaPlayCircle />
          <span>{playerError}</span>
        </div>
      )}
    </div>
  );
};

const Okam = () => {
  const [cameras, setCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [playbackByCamera, setPlaybackByCamera] = useState({});
  const [integrationEnabled, setIntegrationEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadCameras = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);

    try {
      const response = await getOkamCameras();
      setIntegrationEnabled(response.enabled);
      const activeCameras = response.enabled
        ? response.cameras.filter((camera) => camera.enabled)
        : [];
      setCameras(activeCameras);
      setSelectedCameraId((current) => (
        activeCameras.some((camera) => camera.id === current)
          ? current
          : activeCameras[0]?.id || ''
      ));
      setError('');
    } catch {
      setError('Canlı kamera bilgileri şu anda alınamıyor.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCameras();
    const timer = window.setInterval(() => loadCameras({ silent: true }), 60000);
    return () => window.clearInterval(timer);
  }, [loadCameras]);

  const selectedCameraConfig = useMemo(() => {
    return cameras.find((camera) => camera.id === selectedCameraId) || cameras[0] || null;
  }, [cameras, selectedCameraId]);

  const selectedCamera = useMemo(() => {
    if (!selectedCameraConfig) return null;

    const playback = playbackByCamera[selectedCameraConfig.id];
    return {
      ...selectedCameraConfig,
      ...playback,
      status: playback?.status || 'Loading'
    };
  }, [playbackByCamera, selectedCameraConfig]);

  const loadPlayback = useCallback(async (
    cameraId,
    { start = false, restartIfStopped = false } = {}
  ) => {
    if (!cameraId) return;

    try {
      let playback = start
        ? await getOkamPlayback(cameraId)
        : await getOkamStatus(cameraId);
      if (!start && restartIfStopped && normalizeStatus(playback) === 'Stopped') {
        playback = await getOkamPlayback(cameraId);
      }
      setPlaybackByCamera((current) => ({
        ...current,
        [cameraId]: {
          ...playback,
          status: normalizeStatus(playback)
        }
      }));
      setError('');
    } catch {
      setPlaybackByCamera((current) => ({
        ...current,
        [cameraId]: {
          ...(current[cameraId] || {}),
          status: 'Unavailable'
        }
      }));
    }
  }, []);

  useEffect(() => {
    const cameraId = selectedCameraConfig?.id;
    if (!cameraId) return undefined;

    loadPlayback(cameraId, { start: true });
    const timer = window.setInterval(
      () => loadPlayback(cameraId, { restartIfStopped: true }),
      15000
    );
    return () => window.clearInterval(timer);
  }, [loadPlayback, selectedCameraConfig?.id]);

  const selectCamera = (cameraId) => {
    if (!cameraId || cameraId === selectedCameraConfig?.id) return;

    setPlaybackByCamera((current) => ({
      ...current,
      [cameraId]: {
        ...(current[cameraId] || {}),
        status: 'Loading'
      }
    }));
    setSelectedCameraId(cameraId);
  };

  return (
    <main className="okam-page">
      <section className="container okam-content">
        {loading ? (
          <div className="okam-state">
            <span className="okam-spinner" />
            <h2>Canlı yayın hazırlanıyor</h2>
            <p>Kamera bağlantısı kontrol ediliyor.</p>
          </div>
        ) : error ? (
          <div className="okam-state okam-state--error">
            <FaBroadcastTower />
            <h2>Yayına ulaşılamadı</h2>
            <p>{error}</p>
            <button type="button" onClick={() => loadCameras()}>
              <FaSyncAlt /> Tekrar Dene
            </button>
          </div>
        ) : !integrationEnabled || !selectedCamera ? (
          <div className="okam-state">
            <FaBroadcastTower />
            <h2>Aktif yayın bulunmuyor</h2>
            <p>Aile Köyü canlı yayını etkinleştirildiğinde bu sayfada görüntülenecektir.</p>
          </div>
        ) : (
          <div className="okam-grid">
            <div>
              <OkamPlayer camera={selectedCamera} />
              <div className="okam-camera-info">
                <div>
                  <span className={`okam-status okam-status--${selectedCamera.status?.toLowerCase()}`}>
                    {statusLabels[selectedCamera.status] || selectedCamera.status}
                  </span>
                  <h2>{selectedCamera.name}</h2>
                  <p className="okam-location">
                    <FaMapMarkerAlt /> İskenderun
                  </p>
                </div>
                <p>
                  {selectedCamera.status === 'Streaming'
                    ? 'Aile Köyü canlı yayın bağlantısı aktif.'
                    : 'Aile Köyü kamerası hazırlanıyor.'}
                </p>
              </div>
            </div>

            {cameras.length > 1 && (
              <aside className="okam-camera-list">
                <h2>Aile Köyü Kameraları</h2>
                {cameras.map((camera) => (
                  <button
                    type="button"
                    key={camera.id}
                    className={camera.id === selectedCamera.id ? 'is-active' : ''}
                    onClick={() => selectCamera(camera.id)}
                  >
                    <span className="okam-camera-list__icon"><FaBroadcastTower /></span>
                    <span>
                      <strong>{camera.name}</strong>
                      <small>
                        {statusLabels[playbackByCamera[camera.id]?.status] || 'Yayın noktası'}
                      </small>
                    </span>
                  </button>
                ))}
              </aside>
            )}
          </div>
        )}
      </section>
    </main>
  );
};

export default Okam;
